/**
 * RESPONSIBILITY: fetch the contact requests and send an admin's changes back.
 *
 * Never cached for later: no stale time, no cache time. These are people's names and
 * phone numbers, and a list that is a minute out of date is a request somebody thinks has
 * been dealt with. The framework only ever writes SECTIONS to localStorage (see main.tsx),
 * and this is not one, so none of it reaches the disk.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { APP_ROUTES } from '../../app-routes';

export interface ContactRequest {
  id: number; createdAt: number;
  firstName: string; lastName: string; email: string; phone: string;
  describesYou: string; heardAboutUs: string; replyBy: string;
  wants: string[]; message: string;
  /** When it was archived. null is the waiting list. */
  handledAt: number | null;
  note: string;
}

const KEY = ['contact-requests'];

async function send(url: string, method: string, body: unknown): Promise<void> {
  const res = await fetch(url, {
    method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(String(res.status));
}

export function useRequests(enabled: boolean) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });

  const list = useQuery({
    queryKey: KEY, enabled, staleTime: 0, gcTime: 0,
    queryFn: async () => {
      const res = await fetch(APP_ROUTES.contactRequests, { cache: 'no-store' });
      if (!res.ok) throw new Error(String(res.status));
      return (await res.json()) as { requests: ContactRequest[] };
    },
  });

  const done = useMutation({
    mutationFn: ({ id, done: d }: { id: number; done: boolean }) =>
      send(APP_ROUTES.contactHandled(id), 'POST', { done: d }),
    onSuccess: refresh,
  });

  const note = useMutation({
    mutationFn: ({ id, note: n }: { id: number; note: string }) =>
      send(APP_ROUTES.contactNote(id), 'PUT', { note: n }),
    onSuccess: refresh,
  });

  return {
    rows: list.data?.requests, failed: list.isError,
    setDone: (id: number, d: boolean) => done.mutate({ id, done: d }),
    saveNote: (id: number, n: string) => note.mutateAsync({ id, note: n }),
  };
}
